// File: frontend/src/hooks/useEntries.ts
import { useState, useEffect, useCallback, useRef } from "react";
import { EntryModel, PaginationModel } from "../models";
import { fetchEntriesByMatchId } from "../services"; // Assuming this path is correct
import { useTranslation } from "react-i18next"; // ADD THIS LINE
import { ENTRIES_PER_PAGE } from "../constants";
import { useAuth } from "../context/AuthContext";

export const useEntries = (
  matchId: number | null,
  showRookieEntries: boolean = true,
  sort: string = "oldest",
  filterToday: boolean = false,
  rookieOnly: boolean = false,
  keyword: string = "",
  entriesPerPage: number = ENTRIES_PER_PAGE,
  // Page to load, taken from the "?sayfa=" URL parameter by App.tsx (see
  // useUrlPage). Kept in the URL so a match page can be linked to at a
  // specific page, survives a refresh, and yields one indexable address per
  // page instead of one per match.
  initialPage: number = 1
) => {
  const { t } = useTranslation(); // Initialize the translation hook
  const { user } = useAuth(); // BUG FIX: Track user to reload entries on login/logout

  const [entries, setEntries] = useState<EntryModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationModel>({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    pageSize: entriesPerPage,
  });

  // Loading state for the "load more" append operation
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Ticket 1c: sequence number of the most recently STARTED entry request.
  //
  // Every fetch takes a copy of this counter before it awaits and compares it
  // again afterwards. If the counter moved on in the meantime a newer request
  // has been fired — normally because the reader picked another match in the
  // left pane — and this response is stale, so it must not be written to state.
  //
  // Without this guard the responses are applied in arrival order rather than
  // request order: a slow response for the previously open match landing after
  // the fast response for the current one overwrites the list, and the page
  // ends up showing one match's header above another match's entries.
  const requestIdRef = useRef(0);

  const loadEntries = useCallback(
    async (page: number) => {
      // Ticket 1c: claim a sequence number for this request before anything else.
      const requestId = ++requestIdRef.current;
      if (matchId === null) {
        // Clear entries and reset pagination if no match is selected
        setEntries([]);
        setPagination({
          currentPage: 1,
          totalPages: 1,
          totalItems: 0,
          pageSize: entriesPerPage,
        });
        return;
      }

      setLoading(true);
      setError(null);
      try {
        // Pass showRookieEntries and toolbar params to backend for proper server-side filtering
        const response = await fetchEntriesByMatchId(
          matchId,
          page,
          entriesPerPage,
          showRookieEntries,
          sort,
          filterToday,
          rookieOnly,
          keyword
        );

        // Ticket 1c: a newer request was started while this one was in flight —
        // its result is the one the reader is waiting for, so drop this response.
        if (requestId !== requestIdRef.current) return;

        // Replace mode: always replace when navigating pages or changing filters
        setEntries(response.entries);

        // Set pagination based on the response
        setPagination({
          currentPage: response.currentPage, // Adjust if your API uses 0-indexed pages but you want 1-indexed for display
          totalPages: response.totalPages,
          totalItems: response.totalItems,
          pageSize: entriesPerPage, // Keep current pageSize or derive from API response if available
        });
      } catch (err) {
        // Ticket 1c: a stale failure must not surface either — the current match
        // may well be loading fine, and this would wipe its entry list.
        if (requestId !== requestIdRef.current) return;
        // Use the translated error message
        setError(t("error_load_all_entries_failed"));
        setEntries([]); // Clear entries on error as well
        setPagination({
          currentPage: 1,
          totalPages: 1,
          totalItems: 0,
          pageSize: entriesPerPage,
        });
      } finally {
        // Ticket 1c: only the newest request owns the loading flag. Clearing it
        // from a stale one would hide the spinner while the real request runs.
        if (requestId === requestIdRef.current) setLoading(false);
      }
    },
    [matchId, showRookieEntries, sort, filterToday, rookieOnly, keyword, entriesPerPage, t]
  );

  // Ticket 1c: drop the previous match's entries the moment the match changes.
  // The fetch below replaces them anyway, but only once it comes back; until
  // then the old list would still be rendered under the new match's header.
  // Declared before the loading effect so both run in the same commit and the
  // reader never sees an empty-state flash.
  useEffect(() => {
    setEntries([]);
    setPagination((prev) => ({
      ...prev,
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
    }));
  }, [matchId]);

  // BUG FIX: Reload entries when matchId, user, OR showRookieEntries changes
  // This ensures vote data and filtering are correct
  useEffect(() => {
    // Load the requested page whenever matchId, user, or any filter changes.
    // Filter changes reset to page 1 via App.tsx clearing the URL parameter:
    // page 7 of a filtered list is unrelated to page 7 of an unfiltered one.
    loadEntries(initialPage);
  }, [matchId, user?.id, showRookieEntries, sort, filterToday, rookieOnly, keyword, entriesPerPage, initialPage, loadEntries]);

  /**
   * Fetches the next page of entries and APPENDS them to the current list.
   * Does NOT call loadEntries so it won't replace the accumulated list.
   * Called when the user clicks "X entry daha" in the main content area.
   */
  const loadMoreEntries = useCallback(async () => {
    if (matchId === null || isLoadingMore) return;

    const nextPage = pagination.currentPage + 1;
    if (nextPage > pagination.totalPages) return;

    // Ticket 1c: "load more" appends, so a stale response is even worse here —
    // it would mix another match's entries into the current list. Remember which
    // request generation this belongs to and verify it after the round trip.
    const requestId = requestIdRef.current;
    setIsLoadingMore(true);
    try {
      const response = await fetchEntriesByMatchId(
        matchId,
        nextPage,
        entriesPerPage,
        showRookieEntries,
        sort,
        filterToday,
        rookieOnly,
        keyword
      );

      if (requestId !== requestIdRef.current) return;

      // Append new entries to the existing list
      setEntries((prev) => [...prev, ...response.entries]);

      // Advance the pagination page counter without triggering a replace-load
      setPagination((prev) => ({
        ...prev,
        currentPage: nextPage,
        totalPages: response.totalPages,
        totalItems: response.totalItems,
      }));
    } catch {
      // Silent failure — the existing list remains intact
    } finally {
      setIsLoadingMore(false);
    }
  }, [matchId, isLoadingMore, pagination, entriesPerPage, showRookieEntries, sort, filterToday, rookieOnly, keyword]);

  const updateEntries = useCallback(
    (callback: (prevEntries: EntryModel[]) => EntryModel[]) => {
      setEntries(callback);
    },
    []
  );

  return {
    entries,
    loading,
    error,
    pagination,
    loadEntries,
    loadMoreEntries,
    isLoadingMore,
    updateEntries,
  };
};